/**
 * @krisspy-file
 * @type page
 * @name "LocationsList"
 * @title "Locations List"
 * @description "View and manage all locations"
 * @routes ["/locations"]
 * @layout "DashboardLayout"
 * @flowName "location-management"
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  MapPin,
} from 'lucide-react';
import { getLocations } from '../services/locations.service';
import type { Database } from '../lib/database.types';
import { useAuth } from '../contexts/AuthContext';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import { useUrlFilters } from '../lib/useUrlFilters';

type Location = Database['public']['Tables']['locations']['Row'];

export default function LocationsList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();

  // Use URL-based filters for persistence across navigation
  const { getFilter, setFilter } = useUrlFilters({
    defaults: { search: '' }
  });

  // Derive filter values from URL
  const searchQuery = getFilter('search');
  const setSearchQuery = (value: string) => setFilter('search', value);

  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLocations();
  }, [merchantId, locationIds, isPlatformAdmin]);

  async function loadLocations() {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getLocations(
      { merchantId, locationIds, isPlatformAdmin },
      {
        searchQuery: searchQuery || undefined,
      }
    );

    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }

    if (data) {
      setLocations(data);
    }

    setLoading(false);
  }

  const filteredLocations = locations.filter((location) => {
    const matchesSearch = !searchQuery ||
      location.location_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.pickup_address.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  const getStatusBadge = (active: boolean | null) => {
    return active ? 'badge-success' : 'badge-danger';
  };

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="page-header" data-testid="locations-page-header">
        <div className="page-header-title">
          <h1 className="text-3xl font-bold text-primary mb-sm">{t('locations.title')}</h1>
          <p className="text-secondary">{t('locations.description')}</p>
        </div>
        <div className="page-header-actions" data-testid="locations-page-header-actions">
          <button
          onClick={() => navigate('/locations/create')}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4" />
          {t('locations.addLocation')}
        </button>
        </div>
      </div>

      <div className="card">
        <div className="mb-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              type="text"
              placeholder={t('locations.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
        </div>

        {loading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-danger/10 mx-auto mb-lg flex items-center justify-center">
              <MapPin className="w-8 h-8 text-danger" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('locations.errorLoading')}</h3>
            <p className="text-secondary mb-xl">{error}</p>
            <button onClick={loadLocations} className="btn btn-primary">
              {t('common.retry')}
            </button>
          </div>
        ) : filteredLocations.length === 0 ? (
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
              <MapPin className="w-8 h-8 text-secondary" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('locations.noLocationsFound')}</h3>
            <p className="text-secondary mb-xl">
              {searchQuery
                ? t('locations.tryAdjustingSearch')
                : t('locations.getStarted')}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/locations/create')}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4" />
                {t('locations.addLocation')}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-primary">
                <tr>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('locations.locationName')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('locations.address')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('locations.contact')}</th>
                  <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('locations.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredLocations.map((location) => (
                  <tr
                    key={location.id}
                    onClick={() => navigate(`/locations/${location.id}/edit`)}
                    className="hover:bg-secondary transition-colors cursor-pointer"
                  >
                    <td className="py-md px-lg">
                      <div className="flex items-center gap-md">
                        <div className="w-10 h-10 rounded-lg bg-blue/10 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-blue" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-primary">{location.location_name}</p>
                          <p className="text-xs text-secondary">{location.location_slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-md px-lg">
                      <p className="text-sm text-primary max-w-xs truncate">{location.pickup_address?.replace(/<[^>]*>/g, '') || ''}</p>
                    </td>
                    <td className="py-md px-lg">
                      <p className="text-sm text-secondary">{location.location_contact || t('common.na')}</p>
                    </td>
                    <td className="py-md px-lg">
                      <span className={`badge ${getStatusBadge(location.active)}`}>
                        {location.active ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-lg pt-lg border-t border-primary flex items-center justify-between">
          <p className="text-sm text-secondary">
            {t('common.showing')} <span className="font-medium text-primary">{filteredLocations.length}</span> {t('common.of')}{' '}
            <span className="font-medium text-primary">{locations.length}</span> {t('locations.title').toLowerCase()}
          </p>
        </div>
      </div>
    </div>
  );
}
